import { useState } from "react";
import { toast } from "react-toastify";

import { useDeleteOrganizationMutation } from "@graphql/organizations/organizations.g";

import { Box } from "@design-system/Box/Box";
import { Button } from "@design-system/Button/Button";
import { Checkbox } from "@design-system/Checkbox/Checkbox";
import { Modal } from "@design-system/Modal/Modal";
import { Text } from "@design-system/Text/Text";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  organizationName: string;
  onSuccess?: () => void;
};

export const DeleteOrganizationModal = ({
  isOpen,
  onClose,
  organizationId,
  organizationName,
  onSuccess,
}: Props) => {
  const [isChecked, setIsChecked] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { mutateAsync: deleteOrganization } = useDeleteOrganizationMutation();

  const handleClose = () => {
    setIsChecked(false);
    onClose();
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      const response = await deleteOrganization({
        id: organizationId,
      });

      if (response.deleteOrganization?.success) {
        toast.success("Organization deleted successfully");
        if (onSuccess) {
          onSuccess();
        }
        handleClose();
      } else {
        const errorMessage = response.deleteOrganization?.errors?.[0] ?? "Failed to delete organization";
        toast.error(errorMessage);
      }
    } catch {
      toast.error("An unexpected error occurred");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <Modal.Header>
        <Modal.Header.Title>Delete Organization</Modal.Header.Title>
      </Modal.Header>
      <Modal.Body>
        <Box direction="column" gap={16}>
          <Text>
            You are about to delete the organization <strong>{organizationName}</strong>. 
            This action will delete the organization, its dedicated schema, and all associated data.
          </Text>
          <Text weight={700} color="danger">
            This action is irreversible.
          </Text>
          <Box direction="row" gap={8} align="center">
            <Checkbox
              id="confirm-delete"
              checked={isChecked}
              onChange={(e) => setIsChecked(e.target.checked)}
            />
            <label htmlFor="confirm-delete">
              <Text>I understand that this is irreversible</Text>
            </label>
          </Box>
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Box direction="row" gap={8} justify="flex-end">
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleClose}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleDelete}
            disabled={!isChecked || isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </Box>
      </Modal.Footer>
    </Modal>
  );
};
